import { useMemo, useState, useCallback } from 'react';
import { djs } from '@duke-hq/djs';
import { BillableItemsList } from './BillableItems';
import { BillableItemsFilters } from './BillableItemsFilters';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { withPermission } from '../../withPermission';
import { useCheckPermission } from '../../hooks/useCheckPermission';
import { useSearchParams } from 'react-router-dom';
import pickBy from 'lodash-es/pickBy.js';
import { SortOrder, emptyPaginationResponse, parseSortOrder } from '@duke-hq/lib/pagination';
import { Button, ButtonGroup, Center, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import { ArrowRightFromLine, RefreshCw } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { BillableRow, BillableDetails, BillableURLFilters } from './types';
import {
  BILLABLE_ITEM_SORT_FIELDS,
  type BillableItemSortField,
} from '@duke-hq/billing/types/billableItems';

export const BillableItems = withPermission('canViewFinances', () => {
  const canManageXeroInvoices = useCheckPermission('canManageXeroInvoices');
  const { currentPage, pageSize, setPage } = usePagination();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();

  // Default date range (last 5 days)
  const defaultEnd = useMemo(() => djs().endOf('day').toDate(), []);
  const defaultStart = useMemo(() => djs().subtract(5, 'days').startOf('day').toDate(), []);

  const [searchParams, setSearchParams] = useSearchParams({
    fromDate: defaultStart.toISOString(),
    toDate: defaultEnd.toISOString(),
    orderBy: 'createdAt',
    sort: SortOrder.DESC,
    page: '1',
  });

  const [isCreateDraftLoading, setIsCreateDraftLoading] = useState<boolean>(false);
  const [isApproveLoading, setIsApproveLoading] = useState<boolean>(false);
  const [xeroSyncError, setXeroSyncError] = useState<string | null>(null);
  const [comparisonBillableId, setComparisonBillableId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const getURLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { fromDate, toDate, providerIds, status, orderBy, sort, page, courseEnrolmentId } =
      urlFilters;

    const isValidOrderBy = (value: string | undefined): value is BillableItemSortField =>
      value != null && BILLABLE_ITEM_SORT_FIELDS.includes(value as BillableItemSortField);

    return {
      fromDate: fromDate ?? defaultStart.toISOString(),
      toDate: toDate ?? defaultEnd.toISOString(),
      providerIds: providerIds ?? undefined,
      status: status ?? undefined,
      orderBy: isValidOrderBy(orderBy) ? orderBy : undefined,
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
      page: page ?? undefined,
      courseEnrolmentId: courseEnrolmentId ?? undefined,
    };
  }, [searchParams, defaultStart, defaultEnd]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<BillableURLFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value !== '',
      );

      if (updatedFilters.page != null && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const query = trpcAdmin.finances.billing.getInvoices.useQuery(
    {
      filters: getURLFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: providers } = trpcAdmin.providers.all.useQuery();

  const providerOptions = useMemo(() => {
    return (
      providers?.map((provider) => {
        return {
          id: provider.drn,
          value: provider.drn,
          label: provider.name,
        };
      }) || []
    );
  }, [providers]);

  const providerNameByDrn = useMemo(
    () => new Map<string, string>(providerOptions.map((p) => [p.id, p.label])),
    [providerOptions],
  );

  const getProviderName = useCallback(
    (providerDrn: string): string | null => providerNameByDrn.get(providerDrn) ?? null,
    [providerNameByDrn],
  );

  const rows: BillableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((r) => {
        return {
          id: r.id,
          providerDrn: r.providerDrn,
          description: r.description,
          currency: r.currency,
          unitAmount: r.unitAmount,
          status: r.status,
          invoiceDate: r.invoiceDate,
          dueDate: r.dueDate,
          xeroInvoiceNumber: r.xeroInvoiceNumber,
        };
      }),
    [query.data?.items],
  );

  const onReload = useCallback(() => void query.refetch(), [query.refetch]);

  const detailsMap: Record<string, BillableDetails> = useMemo(() => {
    const map: Record<string, BillableDetails> = {};
    for (const item of query.data?.items ?? []) {
      map[item.id] = {
        ...item,
        quantity: item.quantity?.toString(),
        contactName: item.contactName ?? null,
        accountCode: item.accountCode ?? null,
        statusReason: item.statusReason ?? null,
        pushedPayload: item.pushedPayload ?? null,
        authorisePayload: item.authorisePayload ?? null,
        voidPayload: item.voidPayload ?? null,
      };
    }
    return map;
  }, [query.data?.items]);

  const createDraftInvoice = trpcAdmin.finances.billing.createDraftInvoice.useMutation({
    onSuccess() {
      onReload();
    },
  });

  const authoriseDraftInvoice = trpcAdmin.finances.billing.authoriseDraftInvoice.useMutation({
    onSuccess() {
      onReload();
    },
  });

  const comparisonQuery = trpcAdmin.finances.billing.compareWithXero.useQuery(
    { billableId: comparisonBillableId ?? '' },
    { enabled: comparisonBillableId != null },
  );

  const handleCompare = useCallback((billableId: string) => {
    setComparisonBillableId(billableId);
  }, []);

  const onXeroCreateDraft = useCallback(
    async (billableIds: string[]) => {
      if (!billableIds?.length) {
        return;
      }
      setIsCreateDraftLoading(true);
      setXeroSyncError(null);
      try {
        await createDraftInvoice.mutateAsync({ billableIds });
        setIsCreateDraftLoading(false);
        await query.refetch();
      } catch (e) {
        setIsCreateDraftLoading(false);
        setXeroSyncError(
          e instanceof Error ? e.message : t`Failed to create draft invoice(s) in Xero`,
        );
      }
    },
    [createDraftInvoice],
  );

  const onXeroApprove = useCallback(
    async (billableIds: string[]) => {
      if (!billableIds?.length) {
        return;
      }
      setIsApproveLoading(true);
      setXeroSyncError(null);
      try {
        await authoriseDraftInvoice.mutateAsync({ billableIds });
        setIsApproveLoading(false);
        await query.refetch();
      } catch (e) {
        setIsApproveLoading(false);
        setXeroSyncError(
          e instanceof Error ? e.message : t`Failed to approve draft invoice(s) in Xero`,
        );
      }
    },
    [authoriseDraftInvoice],
  );

  const allIds = useMemo(() => rows.map((r) => r.id), [rows]);

  const selectedIdsVisible = useMemo(() => {
    const setIds = new Set(allIds);
    return selectedIds.filter((id) => setIds.has(id));
  }, [selectedIds, allIds]);

  const { eligibleNewIds, eligibleDraftIds, ignoredCount } = useMemo(() => {
    const selectedSet = new Set(selectedIdsVisible);
    const selectedRows = rows.filter((r) => selectedSet.has(r.id));
    const eligibleNew = selectedRows.filter((r) => r.status === 'NEW').map((r) => r.id);
    const eligibleDraft = selectedRows.filter((r) => r.status === 'DRAFT_XERO').map((r) => r.id);
    const eligibleCount = eligibleNew.length + eligibleDraft.length;
    const ignored = Math.max(selectedRows.length - eligibleCount, 0);
    return {
      eligibleNewIds: eligibleNew,
      eligibleDraftIds: eligibleDraft,
      ignoredCount: ignored,
    };
  }, [rows, selectedIdsVisible]);

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded ? (
          <IconButton
            key="toggle"
            aria-label={t`expand secondary sidebar`}
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ) : null,
        <Text key="page-heading" textStyle="2xl" fontWeight="medium">
          <Trans>Billable Items</Trans>
        </Text>,
      ]}
      filters={
        <>
          <BillableItemsFilters
            filters={getURLFilters}
            providerOptions={providerOptions ?? []}
            onChange={handleFilterChange}
            loading={query.isFetching}
          />
          <SearchInput
            maxW="search-input"
            urlSync={true}
            urlKey="courseEnrolmentId"
            placeholder={t`Course enrolment ID`}
            onCommit={(val) => handleFilterChange({ courseEnrolmentId: val || undefined })}
          />
        </>
      }
      tableActionsLeft={
        canManageXeroInvoices && selectedIdsVisible.length > 0 ? (
          <Text fontSize="sm" color="muted">
            {t`${selectedIdsVisible.length} selected · ${eligibleNewIds.length} NEW · ${eligibleDraftIds.length} DRAFT_XERO${
              ignoredCount ? ` · ${ignoredCount} ignored` : ''
            }`}
          </Text>
        ) : null
      }
      tableActionsRight={
        <HStack spacing={2}>
          <Button
            leftIcon={<RefreshCw size={16} />}
            onClick={onReload}
            isDisabled={query.isFetching}
            size="sm"
            variant="outline"
          >
            <Trans>Refresh</Trans>
          </Button>
          {xeroSyncError ? (
            <Text fontSize="sm" color="red.500">
              {xeroSyncError}
            </Text>
          ) : null}
          {canManageXeroInvoices ? (
            <ButtonGroup>
              <Button
                onClick={() => onXeroCreateDraft(eligibleNewIds)}
                isDisabled={
                  selectedIdsVisible.length === 0 ||
                  eligibleNewIds.length === 0 ||
                  isCreateDraftLoading ||
                  isApproveLoading
                }
                isLoading={isCreateDraftLoading}
                loadingText={t`Creating draft(s) in Xero`}
                size="sm"
                variant="solid"
              >
                <Trans>Create Draft in Xero</Trans> ({eligibleNewIds.length})
              </Button>
              <Button
                onClick={() => onXeroApprove(eligibleDraftIds)}
                isDisabled={
                  selectedIdsVisible.length === 0 ||
                  eligibleDraftIds.length === 0 ||
                  isCreateDraftLoading ||
                  isApproveLoading
                }
                isLoading={isApproveLoading}
                loadingText={t`Approving draft(s) in Xero`}
                size="sm"
                variant="solid"
              >
                <Trans>Approve on Xero</Trans> ({eligibleDraftIds.length})
              </Button>
            </ButtonGroup>
          ) : null}
        </HStack>
      }
      table={
        <>
          <BillableItemsList
            rows={rows}
            error={query.error ? t`Failed to load invoices` : null}
            getDetailsById={(id) => detailsMap[id]}
            getProviderName={getProviderName}
            onCompare={handleCompare}
            comparisonData={comparisonQuery.data}
            comparisonLoading={comparisonQuery.isFetching}
            canManageXeroInvoices={canManageXeroInvoices}
            urlParams={getURLFilters}
            onFilters={handleFilterChange}
            selectedIdsVisible={selectedIdsVisible}
            setSelectedIds={setSelectedIds}
          />
          {query.isFetching ? (
            <Center p={8}>
              <Spinner size="xl" transitionDelay={'2900ms'} />
            </Center>
          ) : null}
          {!query.isFetching && rows.length === 0 ? (
            <Center p={8}>
              <Text textAlign="center">
                <Trans>No billable items found</Trans>
              </Text>
            </Center>
          ) : null}
        </>
      }
      pagination={
        rows.length > 0 && query.data?.pagination ? (
          <Pagination pagination={query.data.pagination} onPageChange={setPage} />
        ) : null
      }
    />
  );
});
